// DepartmentManagement.jsx
import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Badge, Alert, Spinner, Modal, Form, Breadcrumb } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaEye, FaUsers, FaFileExcel, FaArrowLeft, FaHome } from 'react-icons/fa';
import MainLayout from '../../layouts/MainLayout';

const DepartmentManagement = () => {
  const navigate = useNavigate();
  // State declarations
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [departmentUsers, setDepartmentUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    manager_id: '',
    parent_department_id: '',
    location: '',
    is_active: true
  });

  // Improved data fetching with better error handling
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log("Memulai fetching data departemen...");

        // ---------- FETCH DEPARTMENTS ----------
        let departmentData = null;
        let departmentFetchSuccessful = false;

        try {
          console.log("Mencoba fetch dari endpoint HRD departments");
          const response = await axios.get('https://rumahakrilik.id/api/hrd/departments/', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` },
            timeout: 10000  // 10 second timeout
          });

          console.log("Response dari API departments:", response.data);

          if (response.data && Array.isArray(response.data)) {
            departmentData = response.data;
            if (departmentData.length > 0) {
              console.log(`Berhasil mendapatkan ${departmentData.length} departments dari API`);
              departmentFetchSuccessful = true;
            } else {
              console.warn("API returned empty department array");
            }
          } else {
            console.warn("API returned unexpected data format:", response.data);
          }
        } catch (err) {
          console.error("Error fetching departments:", err);
          console.error("Error details:", err.response?.data || err.message);
        }

        // If API call failed, use dummy data
        if (!departmentFetchSuccessful) {
          console.log("Using dummy department data");
          departmentData = [
            {
              id: 1,
              name: 'Management',
              description: 'Top level management',
              manager_id: 1,
              manager_name: 'Muhammad Botee',
              location: 'HQ Floor 3',
              is_active: true,
              user_count: 3
            },
            {
              id: 2,
              name: 'Marketing',
              description: 'Marketing department',
              manager_id: 2,
              manager_name: 'Nurul Iman',
              location: 'HQ Floor 2',
              is_active: true,
              user_count: 8
            },
            {
              id: 3,
              name: 'Production',
              description: 'Production department',
              manager_id: 3,
              manager_name: 'Ahmad Production',
              location: 'Factory Building',
              is_active: true,
              user_count: 12
            }
          ];
          setError("Gagal memuat data departemen dari server. Menggunakan data lokal sementara.");
        }

        // ---------- FETCH USERS FOR MANAGER SELECTION ----------
        let userData = null;

        try {
          const usersResponse = await axios.get('https://rumahakrilik.id/api/hrd/users/', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` },
            timeout: 8000
          });

          if (usersResponse.data && Array.isArray(usersResponse.data)) {
            userData = usersResponse.data;
            console.log(`Berhasil mendapatkan ${userData.length} users untuk pilihan manager`);
          }
        } catch (err) {
          console.error("Error fetching users for manager selection:", err);
        }

        // If users API call failed, use minimal dummy data
        if (!userData) {
          userData = [
            { id: 1, username: 'mbotee', first_name: 'Muhammad', last_name: 'Botee' },
            { id: 2, username: 'nuruliman', first_name: 'Nurul', last_name: 'Iman' }
          ];
        }

        // Set data to state with sanitization
        setDepartments(departmentData.map(sanitizeDepartmentData));
        setUsers(userData);

      } catch (err) {
        console.error("Fatal error in data fetching:", err);
        setError("Terjadi kesalahan saat memuat data. Silakan refresh halaman.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Sanitize department data to ensure consistent format
  const sanitizeDepartmentData = (department) => {
    if (!department) return {};

    return {
      id: department.id || Math.random(),
      name: department.name || 'Unnamed Department',
      description: department.description || '',
      manager_id: department.manager_id || null,
      manager_name: department.manager_name || 'Unassigned',
      parent_department_id: department.parent_department_id || null,
      parent_department_name: department.parent_department_name || '',
      location: department.location || '',
      is_active: department.is_active !== undefined ? department.is_active : true,
      user_count: department.user_count || 0
    };
  };

  // Department actions (view, add, edit, delete)
  const handleShowViewModal = (department) => {
    setSelectedDepartment(department);
    setShowViewModal(true);
  };

  const handleShowEditModal = (department) => {
    if (!department) return;

    setSelectedDepartment(department);
    setFormData({
      name: department.name || '',
      description: department.description || '',
      manager_id: department.manager_id || '',
      parent_department_id: department.parent_department_id || '',
      location: department.location || '',
      is_active: department.is_active !== undefined ? department.is_active : true
    });
    setShowEditModal(true);
  };

  const handleShowAddModal = () => {
    setFormData({
      name: '',
      description: '',
      manager_id: '',
      parent_department_id: '',
      location: '',
      is_active: true
    });
    setShowAddModal(true);
  };

  // Show users in department 
  const handleShowDepartmentUsers = async (departmentId) => {
    setLoadingUsers(true);
    setDepartmentUsers([]);
    setShowUsersModal(true);

    try {
      const response = await axios.get(`https://rumahakrilik.id/api/hrd/departments/${departmentId}/users/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
      });

      console.log("Department users response:", response.data);

      if (Array.isArray(response.data)) {
        setDepartmentUsers(response.data);
      } else {
        console.error("Invalid users data format:", response.data);
        toast.error("Format data pegawai tidak valid");
      }
    } catch (err) {
      console.error("Error fetching department users:", err);
      toast.error(`Gagal mengambil data pegawai: ${err.message}`);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Add, update, delete methods
  const handleAddDepartment = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log("Adding department with data:", formData);

      const response = await axios.post('https://rumahakrilik.id/api/hrd/departments/',
        {
          name: formData.name,
          description: formData.description,
          manager_id: formData.manager_id || null,
          parent_department_id: formData.parent_department_id || null,
          location: formData.location,
          is_active: formData.is_active
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log("Department added:", response.data);

      // Update departments state
      setDepartments([...departments, sanitizeDepartmentData(response.data)]);
      toast.success('Departemen berhasil ditambahkan!');
      setShowAddModal(false);
    } catch (err) {
      console.error('Error adding department:', err);
      toast.error(`Gagal menambahkan departemen: ${err.message}`);

      // Fallback - update UI anyway if we're in a demo
      if (error) {
        const newDepartment = {
          id: Math.max(...departments.map(d => d.id || 0), 0) + 1,
          name: formData.name,
          description: formData.description,
          manager_id: formData.manager_id,
          manager_name: users.find(u => u.id === parseInt(formData.manager_id))?.first_name || 'Unknown',
          parent_department_id: formData.parent_department_id,
          parent_department_name: departments.find(d => d.id === parseInt(formData.parent_department_id))?.name || '',
          location: formData.location,
          is_active: formData.is_active,
          user_count: 0
        };

        setDepartments([...departments, sanitizeDepartmentData(newDepartment)]);
        toast.info('Departemen ditambahkan secara lokal (server tidak tersedia)');
        setShowAddModal(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!selectedDepartment?.id) {
      toast.error('Department ID is missing. Cannot update department.');
      setLoading(false);
      return;
    }

    try {
      console.log("Updating department:", selectedDepartment.id, "with data:", formData);

      const response = await axios.put(
        `https://rumahakrilik.id/api/hrd/departments/${selectedDepartment.id}/`,
        {
          name: formData.name,
          description: formData.description,
          manager_id: formData.manager_id || null,
          parent_department_id: formData.parent_department_id || null,
          location: formData.location,
          is_active: formData.is_active
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log("Department updated:", response.data);

      // Update departments state
      setDepartments(departments.map(dept =>
        dept.id === selectedDepartment.id ? sanitizeDepartmentData(response.data) : dept
      ));

      toast.success('Departemen berhasil diperbarui!');
      setShowEditModal(false);
    } catch (err) {
      console.error('Error updating department:', err);
      toast.error(`Gagal memperbarui departemen: ${err.message}`);

      // Fallback - update UI anyway if we're in demo mode
      if (error) {
        const updatedDepartment = {
          ...selectedDepartment,
          name: formData.name,
          description: formData.description,
          manager_id: formData.manager_id,
          manager_name: users.find(u => u.id === parseInt(formData.manager_id))?.first_name || 'Unknown',
          parent_department_id: formData.parent_department_id,
          parent_department_name: departments.find(d => d.id === parseInt(formData.parent_department_id))?.name || '',
          location: formData.location,
          is_active: formData.is_active
        };

        setDepartments(departments.map(dept =>
          dept.id === selectedDepartment.id ? sanitizeDepartmentData(updatedDepartment) : dept
        ));

        toast.info('Departemen diperbarui secara lokal (server tidak tersedia)');
        setShowEditModal(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDepartment = async (departmentId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus departemen ini?')) {
      return;
    }

    setLoading(true);

    try {
      await axios.delete(`https://rumahakrilik.id/api/hrd/departments/${departmentId}/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
      });

      // Update departments state
      setDepartments(departments.filter(dept => dept.id !== departmentId));
      toast.success('Departemen berhasil dihapus!');
    } catch (err) {
      console.error('Error deleting department:', err);
      toast.error(`Gagal menghapus departemen: ${err.message}`);

      // Fallback - update UI anyway if we're in demo mode
      if (error) {
        setDepartments(departments.filter(dept => dept.id !== departmentId));
        toast.info('Departemen dihapus secara lokal (server tidak tersedia)');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Export to Excel
  const exportToExcel = () => {
    // Implementation for Excel export
    toast.info('Expor ke Excel akan tersedia dalam update berikutnya.');
  };

  // Add this function to navigate back to dashboard
  const handleBackToDashboard = () => {
    navigate('/dashboard/hrd');
  };

  return (
    <MainLayout>
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Manajemen Departemen</h2>
          <div>
            <Button
              variant="outline-secondary"
              className="me-2"
              onClick={handleBackToDashboard}
            >
              <FaArrowLeft className="me-1" /> Kembali ke Dashboard HRD
            </Button>
            <Button variant="success" className="me-2" onClick={handleShowAddModal}>
              <FaPlus className="me-1" /> Tambah Departemen
            </Button>
            <Button variant="outline-success" onClick={exportToExcel}>
              <FaFileExcel className="me-1" /> Export Excel
            </Button>
          </div>
        </div>

        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>
            <FaHome /> Dashboard
          </Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard/hrd" }}>
            HRD
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Manajemen Departemen</Breadcrumb.Item>
        </Breadcrumb>

        {error && <Alert variant="warning">{error}</Alert>}

        <Card>
          <Card.Header className="bg-white">
            <h3>Daftar Departemen</h3>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center p-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3">Memuat data departemen...</p>
              </div>
            ) : (
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>Nama</th>
                    <th>Deskripsi</th>
                    <th>Manager</th>
                    <th>Lokasi</th>
                    <th>Jumlah Pegawai</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.length > 0 ? (
                    departments.map(dept => (
                      <tr key={dept.id}>
                        <td>{dept.name}</td>
                        <td>{dept.description || '-'}</td>
                        <td>{dept.manager_name || 'Belum ditentukan'}</td>
                        <td>{dept.location || '-'}</td>
                        <td>
                          <Badge
                            bg="info"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleShowDepartmentUsers(dept.id)}
                          >
                            {dept.user_count || 0} pegawai
                          </Badge>
                        </td>
                        <td>
                          {dept.is_active ? (
                            <Badge bg="success">Aktif</Badge>
                          ) : (
                            <Badge bg="danger">Non-Aktif</Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="me-1"
                            onClick={() => handleShowViewModal(dept)}
                          >
                            <FaEye /> Detail
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleShowEditModal(dept)}
                          >
                            <FaEdit /> Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteDepartment(dept.id)}
                          >
                            <FaTrash /> Hapus
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        Tidak ada data departemen.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* View Department Modal */}
        <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Detail Departemen</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedDepartment && (
              <div>
                <h4>{selectedDepartment.name}</h4>
                <hr />
                <Table borderless>
                  <tbody>
                    <tr>
                      <td width="30%"><strong>Deskripsi:</strong></td>
                      <td>{selectedDepartment.description || '-'}</td>
                    </tr>
                    <tr>
                      <td><strong>Manager:</strong></td>
                      <td>{selectedDepartment.manager_name || 'Belum ditentukan'}</td>
                    </tr>
                    <tr>
                      <td><strong>Departemen Induk:</strong></td>
                      <td>{selectedDepartment.parent_department_name || '-'}</td>
                    </tr>
                    <tr>
                      <td><strong>Lokasi:</strong></td>
                      <td>{selectedDepartment.location || '-'}</td>
                    </tr>
                    <tr>
                      <td><strong>Status:</strong></td>
                      <td>
                        {selectedDepartment.is_active ? (
                          <Badge bg="success">Aktif</Badge>
                        ) : (
                          <Badge bg="danger">Non-Aktif</Badge>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Jumlah Pegawai:</strong></td>
                      <td>{selectedDepartment.user_count || 0} pegawai</td>
                    </tr>
                  </tbody>
                </Table>

                <div className="mt-3">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowViewModal(false);
                      handleShowDepartmentUsers(selectedDepartment.id);
                    }}
                  >
                    <FaUsers className="me-1" /> Lihat Pegawai
                  </Button>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowViewModal(false)}>
              Tutup
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Add/Edit Department Modal - Shared component */}
        <Modal
          show={showAddModal || showEditModal}
          onHide={() => showAddModal ? setShowAddModal(false) : setShowEditModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {showAddModal ? 'Tambah Departemen Baru' : 'Edit Departemen'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={showAddModal ? handleAddDepartment : handleUpdateDepartment}>
              <Form.Group className="mb-3">
                <Form.Label>Nama Departemen</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Deskripsi</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Manager</Form.Label>
                <Form.Select
                  name="manager_id"
                  value={formData.manager_id}
                  onChange={handleChange}
                >
                  <option value="">- Pilih Manager -</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.username})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Departemen Induk (Opsional)</Form.Label>
                <Form.Select
                  name="parent_department_id"
                  value={formData.parent_department_id}
                  onChange={handleChange}
                >
                  <option value="">- Tidak Ada -</option>
                  {departments
                    .filter(dept => !selectedDepartment || dept.id !== selectedDepartment.id)
                    .map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))
                  }
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Lokasi</Form.Label>
                <Form.Control
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Departemen Aktif"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button
                  variant="secondary"
                  className="me-2"
                  onClick={() => showAddModal ? setShowAddModal(false) : setShowEditModal(false)}
                >
                  Batal
                </Button>
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-1"
                      />
                      Menyimpan...
                    </>
                  ) : (
                    <>Simpan</>
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Department Users Modal */}
        <Modal show={showUsersModal} onHide={() => setShowUsersModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Pegawai Departemen {departments.find(d => d.id === selectedDepartment?.id)?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingUsers ? (
              <div className="text-center p-3">
                <Spinner animation="border" role="status" />
                <p className="mt-2">Memuat daftar pegawai...</p>
              </div>
            ) : (
              <>
                {departmentUsers.length > 0 ? (
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Nama</th>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departmentUsers.map(user => (
                        <tr key={user.id}>
                          <td>
                            {user.first_name || user.last_name ?
                              `${user.first_name || ''} ${user.last_name || ''}`.trim() :
                              '-'}
                          </td>
                          <td>{user.username}</td>
                          <td>{user.email || '-'}</td>
                          <td>{user.role?.name || '-'}</td>
                          <td>
                            {user.is_active ? (
                              <Badge bg="success">Aktif</Badge>
                            ) : (
                              <Badge bg="danger">Non-Aktif</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center p-4">
                    <p>Tidak ada pegawai dalam departemen ini.</p>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUsersModal(false)}>
              Tutup
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </MainLayout>
  );
};

export default DepartmentManagement;